import { ComponentType, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { TCreditCardFormSchema } from '@/common/components/CreditCardForm/CreditCardForm.schema';
import { setPaymentMethodsQueryData } from '@/common/integrations/menhir/employee/payments/payments.hooks';
import {
  getPaymentMethodIntent,
  getPaymentMethods,
} from '@/common/integrations/menhir/employee/payments/payments.service';
import { IEmployeePaymentMethod } from '@/common/integrations/menhir/employee/payments/payments.types';
import usePolling from '@/web/hooks/usePolling';
import {
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import * as StripeJS from '@stripe/stripe-js';
import { ConfirmCardSetupData } from '@stripe/stripe-js';

export type TStripeProviderProps = {
  children: ReactElement;
};
const envKey = process.env.EXPO_PUBLIC_STRIPE_KEY;

const fonts = [
  {
    cssSrc: 'https://static.coverflex.com/fonts/fonts.css',
  },
];

export const StripeProvider = ({ children }: TStripeProviderProps) => {
  const { i18n } = useTranslation();
  const config = envKey ? StripeJS.loadStripe(envKey) : null;
  const stripeLocale = (i18n.language.split('-')[0] ??
    'en') as StripeJS.StripeElementLocale;

  return config ? (
    <Elements
      stripe={config}
      options={{
        locale: stripeLocale,
        fonts,
      }}
    >
      {children}
    </Elements>
  ) : (
    children
  );
};

export const withStripe = <T extends {}>(Component: ComponentType<T>) => {
  const WithStripe = (props: T) => (
    <StripeProvider>
      <Component {...props} />
    </StripeProvider>
  );

  return WithStripe;
};

export function useSetupPaymentMethod() {
  const stripe = useStripe();
  const elements = useElements();
  const poll = usePolling<IEmployeePaymentMethod>(4);

  const setupPaymentMethod = async ({
    cardHolderName,
    country,
    zipCode,
  }: TCreditCardFormSchema) => {
    if (!stripe) {
      throw new Error('Stripe not found');
    }

    if (!elements) {
      throw new Error('Stripe elements not found');
    }

    const card = elements.getElement(CardNumberElement);

    if (!card) {
      throw new Error('Card number element not found');
    }

    const data: ConfirmCardSetupData = {
      payment_method: {
        card,
        billing_details: {
          name: cardHolderName,
          address: {
            country: country.iso2,
            postal_code: zipCode,
          },
        },
      },
    };

    const { clientSecret } = await getPaymentMethodIntent();

    const { error, setupIntent } = await stripe.confirmCardSetup(
      clientSecret,
      data
    );

    if (error) {
      throw new Error(error.message);
    }

    const paymentMethod = await poll(async () => {
      const methods = await getPaymentMethods();
      const paymentMethod = methods.find(
        (method) =>
          method.providerPaymentMethodId === setupIntent.payment_method
      );

      if (paymentMethod) {
        setPaymentMethodsQueryData(methods);
        return paymentMethod;
      } else throw new Error();
    });

    return paymentMethod;
  };

  return { setupPaymentMethod };
}
